import { Home, LocationOn, Refresh } from "@mui/icons-material";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import useWindowSize from "../../utils/useWindowSize";
import { renderToString } from "react-dom/server";
import { MapContainer, TileLayer, Marker, Tooltip as LeaftletTooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "./map.css";
import isDarkColor from "../../utils/isDarkColor";
import { useTranslation } from "react-i18next";
import zIndices from "../../assets/zIndices";
import useMapHeight from "./useMapHeight";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const RefreshButton = ({ onClick, t }) => {
  return (
    <Tooltip title={t("map.refresh")}>
      <Box
        sx={{
          backgroundColor: "white",
          boxShadow: 4,
          borderRadius: "4px",
          padding: "4px",
          display: "inline-block",
        }}
      >
        <IconButton aria-label='refresh' color='primary' onClick={onClick}>
          <Refresh />
        </IconButton>
      </Box>
    </Tooltip>
  );
};

const HomeButton = ({ bounds, t }) => {
  const map = useMap();
  const handleClick = () => {
    if (bounds) {
      map.fitBounds(bounds);
    }
  };
  return (
    <Tooltip title={t("map.home")}>
      <Box
        sx={{
          backgroundColor: "white",
          boxShadow: 4,
          borderRadius: "4px",
          padding: "4px",
          display: "inline-block",
        }}
      >
        <IconButton aria-label='home' color='primary' onClick={handleClick}>
          <Home />
        </IconButton>
      </Box>
    </Tooltip>
  );
};

const MapInitializer = ({ onBoundsCalculated, locations }) => {
  const map = useMap();
  useEffect(() => {
    if (locations && locations.length > 0) {
      const bounds = locations.map((location) => location.position);
      map.fitBounds(bounds);
      if (onBoundsCalculated) {
        onBoundsCalculated(bounds);
      }
    }
  }, [map, onBoundsCalculated, locations]);
  return null;
};

function MapComponent({ locations, fetchData, renderTooltip, onLocationsClick }) {
  const [initialBounds, setInitialBounds] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = () => {
    fetchData();
  };
  const mapHeight = useMapHeight();

  return (
    <div style={{ height: mapHeight, width: "100%" }}>
      <MapContainer style={{ height: "100%" }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        />
        <MapInitializer onBoundsCalculated={setInitialBounds} locations={locations} />
        <Box
          sx={{
            position: "absolute",
            backgroundColor: "transparent",
            top: "10px",
            right: "10px",
            zIndex: zIndices.map,
            borderRadius: "4px",
            padding: "8px",
            display: "flex",
            gap: 1,
          }}
        >
          <RefreshButton onClick={handleRefresh} t={t} />
          <HomeButton bounds={initialBounds} t={t} />
        </Box>

        {locations.map((location, idx) => (
          <Marker
            key={location.id}
            position={location.position}
            icon={createMuiIcon({ color: colors[idx] })}
            eventHandlers={{
              click: onLocationsClick ? () => onLocationsClick(location) : () => {},
            }}
          >
            <LeaftletTooltip>
              {renderTooltip ? renderTooltip(location) : location?.tooltipText}
            </LeaftletTooltip>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
export default MapComponent;

const createMuiIcon = ({ color }) => {
  const box = (
    <Box
      sx={{
        // Set background to transparent
        backgroundColor: "transparent",
        width: 30,
        height: 30,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <LocationOn
        style={{
          color: color,
          stroke: isDarkColor(color) ? "white" : "black",
          strokeWidth: 1,
        }}
      />
    </Box>
  );
  const htmlString = renderToString(box);
  const iconDiv = document.createElement("div");
  iconDiv.innerHTML = htmlString;
  return L.divIcon({
    html: iconDiv,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    className: "custom-mui-icon",
  });
};

const colors = [
  "#00008B", // Dark Blue
  "#0000FF", // Blue
  "#1E90FF", // Dodger Blue
  "#6495ED", // Cornflower Blue
  "#87CEEB", // Sky Blue
  "#ADD8E6", // Light Blue
  "#C5D8DEFF", // White Blue
  // Green colors
  "#006400", // Dark Green
  "#008000", // Green
  "#228B22", // Forest Green
  "#3CB371", // Medium Sea Green
  "#00FF00", // Lime Green
  "#7CFC00", // Lawn Green
  "#ADFF2F", // Green Yellow
  // Red colors
  "#8B0000", // Dark Red
  "#FF0000", // Red
  "#FF4500", // Orange Red
  "#FF6347", // Tomato
  "#FF7F50", // Coral
  "#FFA07A", // Light Salmon
  "#FFE4B5", // Moccasin
  // Purple colors
  "#800080", // Purple
  "#9932CC", // Dark Orchid
  "#BA55D3", // Medium Orchid
  "#DDA0DD", // Plum
  "#EE82EE", // Violet
  "#FF00FF", // Magenta
  "#FFB6C1", // Light Pink
  // Orange colors
  "#FF8C00", // Dark Orange
  "#FFA500", // Orange
  "#FFD700", // Gold
  "#FFFF00", // Yellow
  "#FFFFE0", // Light Yellow
  "#FAFAD2", // Light Goldenrod Yellow
  "#FFEFD5", // Papaya Whip
];
